import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X, ArrowRight, Check } from 'lucide-react';

const OnboardingTour = ({ onComplete, role = 'parent' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    // Check if user has completed onboarding
    const hasCompletedOnboarding = localStorage.getItem('onboarding_completed');
    if (!hasCompletedOnboarding) {
      setShowTour(true);
    }
  }, []);

  const isProfessional = ['attorney', 'court_rep', 'provider'].includes(role);

  const professionalSteps = [
    {
      title: '👋 Welcome to FamilyTrails!',
      description: 'Your case management platform',
      content: 'FamilyTrails gives you documented, court-admissible records of your clients\' co-parenting communication, scheduling, and expenses, all in one secure place.',
      highlight: null
    },
    {
      title: '✉️ Invite Your Clients',
      description: 'Parents join by your invitation',
      content: 'Use "Invite Parent" in the sidebar to bring a client onto the platform. Enter their name and email, attach a case, and they receive an invitation that expires in 7 days. Opposing counsel can be notified automatically.',
      highlight: 'invite-parent'
    },
    {
      title: '📁 Case Files',
      description: 'Everything about a case in one place',
      content: 'Open Case Files to review each client\'s communication records, calendar compliance, expenses, and clinical scoring. Privileged material stays walled off from the opposing side.',
      highlight: 'case-files'
    },
    {
      title: '📊 Reports & Records',
      description: 'Court-ready documentation',
      content: 'Generate timestamped reports of messages, GPS check-ins, and compliance history that are suitable for court proceedings.',
      highlight: 'reports'
    },
    {
      title: '📱 Use It on Your Phone',
      description: 'Install FamilyTrails like an app',
      content: 'On iPhone: open this site in Safari, tap Share, then "Add to Home Screen". On Android: open it in Chrome, tap the menu, then "Install app". Full install steps are in Help & FAQ.',
      highlight: 'help'
    },
    {
      title: '🎉 You\'re All Set!',
      description: 'Start managing your cases',
      content: 'Explore at your own pace. The Help & FAQ page in the sidebar covers every feature in plain English.',
      highlight: null
    }
  ];

  const parentSteps = [
    {
      title: '👋 Welcome to FamilyTrails!',
      description: 'Your comprehensive co-parenting platform',
      content: 'FamilyTrails helps you manage schedules, expenses, documents, and communication with your co-parent in one secure place.',
      highlight: null
    },
    {
      title: '📅 Calendar & Events',
      description: 'Never miss important dates',
      content: 'Create events, set reminders, and share your schedule with your co-parent. Use GPS check-ins to confirm attendance.',
      highlight: 'calendar'
    },
    {
      title: '💬 Secure Messaging',
      description: 'Communicate effectively',
      content: 'Send messages with AI-powered tone analysis that helps keep conversations positive and productive.',
      highlight: 'messages'
    },
    {
      title: '📄 Document Management',
      description: 'Store important files securely',
      content: 'Upload medical records, legal documents, school files, and more. Share specific documents with your co-parent or professionals.',
      highlight: 'documents'
    },
    {
      title: '💰 Expense Tracking',
      description: 'Manage shared expenses',
      content: 'Track expenses, split costs, and maintain transparent financial records for your children.',
      highlight: 'expenses'
    },
    {
      title: '📊 Analytics Dashboard',
      description: 'Track your engagement',
      content: 'View insights into your communication patterns, attendance rates, and overall co-parenting engagement. Generate court-admissible reports.',
      highlight: 'analytics'
    },
    {
      title: '📱 Use It on Your Phone',
      description: 'Install FamilyTrails like an app',
      content: 'On iPhone: open this site in Safari, tap Share, then "Add to Home Screen". On Android: open it in Chrome, tap the menu, then "Install app". Full install steps are in Help & FAQ.',
      highlight: 'help'
    },
    {
      title: '🎉 You\'re All Set!',
      description: 'Start using FamilyTrails',
      content: 'Explore all features at your own pace. You can always access help from the sidebar or contact support.',
      highlight: null
    }
  ];

  const steps = isProfessional ? professionalSteps : parentSteps;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handleSkip = () => {
    completeTour();
  };

  const completeTour = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setShowTour(false);
    if (onComplete) onComplete();
  };

  if (!showTour) return null;

  const step = steps[currentStep];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <Card className="w-full max-w-lg mx-4 shadow-2xl">
        <CardHeader className="relative">
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Skip tour"
          >
            <X className="h-5 w-5" />
          </button>
          <CardTitle className="text-2xl">{step.title}</CardTitle>
          <CardDescription className="text-base">{step.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-slate-600 leading-relaxed">{step.content}</p>
          
          {/* Progress Indicators */}
          <div className="flex justify-center space-x-2">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`h-2 rounded-full transition-all ${
                  idx === currentStep
                    ? 'w-8 bg-indigo-600'
                    : idx < currentStep
                    ? 'w-2 bg-indigo-400'
                    : 'w-2 bg-slate-300'
                }`}
              />
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-4">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="text-slate-600"
            >
              Skip Tour
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-500">
                {currentStep + 1} / {steps.length}
              </span>
              <Button onClick={handleNext}>
                {currentStep === steps.length - 1 ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Get Started
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingTour;
